// Casca da timeline — estrutura do #timelinePanel do edvid: calha de ícones fixa
// (position: sticky, NENHUM ancestral com overflow:hidden entre ela e o painel),
// régua em canvas do tamanho da janela, agulha e scrub no fundo.
// Os dois aprendizados do topo do app.js valem aqui: o pointerdown do painel
// ignora a calha e os botões (senão o setPointerCapture engole o clique), e a
// agulha é escrita direto no DOM (ver playhead.ts).
import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode, type RefObject,
} from 'react';
import { LABEL_W, fmt } from './edvidTime';
import type { Playhead } from './playhead';

interface TimelineCtx {
  pps: number;
  panelRef: RefObject<HTMLDivElement>;
  showTip: (x: number, y: number, content: ReactNode) => void;
  hideTip: () => void;
}
const Ctx = createContext<TimelineCtx | null>(null);
export function useTimeline(): TimelineCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTimeline fora de <TimelineShell>');
  return v;
}

/** Canvas do tamanho da JANELA visível da faixa, redesenhado em scroll/zoom (drawRuler/drawWave do edvid). */
export function useViewportCanvas(
  canvasRef: RefObject<HTMLCanvasElement>,
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number, x0: number) => void,
  deps: unknown[],
) {
  const { panelRef } = useTimeline();
  const drawRef = useRef(draw);
  drawRef.current = draw;
  const paint = useCallback(() => {
    const cv = canvasRef.current;
    const panel = panelRef.current;
    if (!cv || !panel || !cv.parentElement) return;
    const dpr = window.devicePixelRatio || 1;
    const w = panel.clientWidth;
    const h = cv.parentElement.clientHeight;
    cv.width = Math.max(1, Math.round(w * dpr));
    cv.height = Math.max(1, Math.round(h * dpr));
    cv.style.width = `${w}px`;
    cv.style.height = `${h}px`;
    const x0 = Math.max(0, panel.scrollLeft - LABEL_W);
    cv.style.left = `${x0}px`;
    const ctx = cv.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    drawRef.current(ctx, w, h, x0);
  }, [canvasRef, panelRef]);

  useEffect(() => {
    paint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    let raf = 0;
    const onScroll = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(paint); };
    panel.addEventListener('scroll', onScroll);
    const ro = new ResizeObserver(onScroll);
    ro.observe(panel);
    return () => { panel.removeEventListener('scroll', onScroll); ro.disconnect(); cancelAnimationFrame(raf); };
  }, [panelRef, paint]);
}

export function TrackRow({ icon, title, height = 30, tone = 'slate', children, labelExtra, className = '' }: {
  icon?: ReactNode;
  title?: string;
  height?: number;
  tone?: 'slate' | 'indigo' | 'teal' | 'olive' | 'amber' | 'rose' | 'violet';
  children?: ReactNode;
  labelExtra?: ReactNode;
  className?: string;
}) {
  const toneCls: Record<string, string> = {
    slate: 'text-slate-300 bg-white/[0.06]',
    indigo: 'text-indigo-300 bg-indigo-500/15',
    teal: 'text-teal-300 bg-teal-500/15',
    olive: 'text-lime-300 bg-lime-500/10',
    amber: 'text-amber-300 bg-amber-500/15',
    rose: 'text-rose-300 bg-rose-500/15',
    violet: 'text-violet-300 bg-violet-500/15',
  };
  return (
    <div className={`relative flex ${className}`} style={{ height }}>
      <div
        className="edvid-track-label sticky left-0 z-30 flex shrink-0 items-center justify-center border-r border-white/10 bg-[#0b1020]"
        style={{ width: LABEL_W }}
        title={title}
      >
        {labelExtra ?? (icon ? <span className={`flex h-6 w-7 items-center justify-center rounded-lg ${toneCls[tone]}`}>{icon}</span> : null)}
      </div>
      <div className="relative min-w-0 flex-1">{children}</div>
    </div>
  );
}

function Ruler({ height = 22 }: { height?: number }) {
  const { pps } = useTimeline();
  const cv = useRef<HTMLCanvasElement>(null);
  useViewportCanvas(cv, (ctx, w, h, x0) => {
    const t0 = x0 / pps;
    const t1 = (x0 + w) / pps;
    const steps = [0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120];
    const step = steps.find(s => s * pps >= 56) || 300;
    ctx.font = '600 9.5px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
    ctx.fillStyle = 'rgba(148,163,184,0.85)';
    ctx.strokeStyle = 'rgba(255,255,255,0.14)';
    for (let t = Math.floor(t0 / step) * step; t <= t1; t += step) {
      if (t < 0) continue;
      const x = t * pps - x0;
      ctx.beginPath(); ctx.moveTo(x, h - 7); ctx.lineTo(x, h); ctx.stroke();
      ctx.fillText(fmt(t), x + 3, h - 9);
      const minor = step / 5;
      for (let m = 1; m < 5; m++) {
        const xm = (t + m * minor) * pps - x0;
        ctx.beginPath(); ctx.moveTo(xm, h - 3.5); ctx.lineTo(xm, h); ctx.stroke();
      }
    }
  }, [pps]);
  return (
    <TrackRow height={height}>
      <canvas ref={cv} className="pointer-events-none absolute top-0" />
    </TrackRow>
  );
}

export function TimelineShell({ duration, pps, panelRef, playhead, onSeek, children, className = '' }: {
  duration: number;
  pps: number;
  panelRef: RefObject<HTMLDivElement>;
  playhead: Playhead;
  onSeek: (t: number) => void;
  children: ReactNode;
  className?: string;
}) {
  const innerRef = useRef<HTMLDivElement>(null);
  const needleRef = useRef<HTMLDivElement>(null);
  const scrubbing = useRef(false);
  const [tip, setTip] = useState<{ x: number; y: number; content: ReactNode } | null>(null);
  const ppsRef = useRef(pps);
  ppsRef.current = pps;

  // agulha + rolagem automática durante o play (mantém a agulha visível)
  useEffect(() => {
    const place = (t: number) => {
      const needle = needleRef.current;
      const panel = panelRef.current;
      if (!needle || !panel) return;
      const x = LABEL_W + t * ppsRef.current;
      needle.style.transform = `translateX(${x}px)`;
      needle.style.visibility = x < panel.scrollLeft + LABEL_W - 1 ? 'hidden' : '';
      if (playhead.playing && !scrubbing.current) {
        const right = panel.scrollLeft + panel.clientWidth;
        if (x > right - 80) panel.scrollLeft = x - panel.clientWidth * 0.25;
      }
    };
    place(playhead.get());
    return playhead.subscribe(place);
  }, [playhead, panelRef, pps]);

  const timeAt = (clientX: number) => {
    const rect = innerRef.current?.getBoundingClientRect();
    if (!rect) return 0;
    return Math.max(0, Math.min(duration, (clientX - rect.left - LABEL_W) / ppsRef.current));
  };

  const ctx: TimelineCtx = {
    pps,
    panelRef,
    showTip: (x, y, content) => setTip({ x, y, content }),
    hideTip: () => setTip(null),
  };

  return (
    <Ctx.Provider value={ctx}>
      <div
        ref={panelRef}
        className={`edvid-scroll relative overflow-x-auto overflow-y-auto rounded-2xl border border-white/10 bg-[#0b1020] ${className}`}
        onPointerDown={e => {
          const target = e.target as HTMLElement;
          if (target.closest('.edvid-track-label') || target.closest('button') || target.closest('[data-tl-item]')) return;
          if (e.button !== 0) return;
          scrubbing.current = true;
          onSeek(timeAt(e.clientX));
          try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* toque sintético */ }
        }}
        onPointerMove={e => { if (scrubbing.current) onSeek(timeAt(e.clientX)); }}
        onPointerUp={() => { scrubbing.current = false; }}
        onPointerCancel={() => { scrubbing.current = false; }}
      >
        <div ref={innerRef} className="relative pb-2" style={{ width: LABEL_W + Math.max(duration, 1) * pps + 14, minWidth: '100%' }}>
          <Ruler />
          <div className="flex flex-col gap-[3px]">{children}</div>
          <div
            ref={needleRef}
            className="pointer-events-none absolute bottom-0 left-0 top-0 z-20 w-px bg-rose-400"
            style={{ willChange: 'transform' }}
          >
            <div className="absolute -left-[5px] top-0 h-0 w-0 border-x-[5.5px] border-t-[7px] border-x-transparent border-t-rose-400" />
          </div>
        </div>
      </div>
      {tip && (
        <div
          className="pointer-events-none fixed z-[2147483200] rounded-lg border border-white/10 bg-slate-950/95 px-2.5 py-1.5 font-mono text-[11px] text-slate-100 shadow-xl"
          style={{ left: tip.x + 14, top: tip.y - 38 }}
        >
          {tip.content}
        </div>
      )}
    </Ctx.Provider>
  );
}
